import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useMap } from '../map/MapContext';
import {
  addTrackLayer,
  removeTrackLayer,
  fitTrackOnMap,
  setOwnshipAtFraction,
} from '../map/FlightTrackLayer';
import {
  importTrack,
  listTracks,
  loadTrack,
  deleteTrack,
} from '../services/TrackImporter';
import { useThreeD } from '../map/ThreeDViewContext';
import type { Track } from '../services/track-parsers/track-types';

type TrackSummary = Awaited<ReturnType<typeof listTracks>>[number];

function fmtDistance(nm: number): string { return `${nm.toFixed(1)} NM`; }
function fmtDuration(ms: number): string {
  if (ms <= 0) return '\u2014';
  const s = Math.round(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return h > 0 ? `${h}h ${m}m ${sec}s` : `${m}m ${sec}s`;
}
function fmtAlt(ft: number): string { return `${Math.round(ft).toLocaleString()} ft`; }
function fmtSpeed(kts: number): string { return `${Math.round(kts)} kt`; }

export const FlightReviewPanel: React.FC = () => {
  const { map } = useMap();
  const threeD = useThreeD();
  const [active, setActive] = useState<Track | null>(null);
  const [summaries, setSummaries] = useState<TrackSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [scrub, setScrub] = useState(0); // 0..1
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cesiumPathCleanupRef = useRef<(() => void) | null>(null);

  // Whenever the active track or 3D-ready state changes, (re)render the 3D path.
  useEffect(() => {
    // Always tear down any previous 3D path first.
    if (cesiumPathCleanupRef.current) {
      cesiumPathCleanupRef.current();
      cesiumPathCleanupRef.current = null;
    }
    if (!active || !threeD.ready) return;
    let cancelled = false;
    (async () => {
      const cleanup = await threeD.showFlightPath(active);
      if (cancelled) { cleanup(); return; }
      cesiumPathCleanupRef.current = cleanup;
    })();
    return () => { cancelled = true; };
  }, [active, threeD.ready, threeD.showFlightPath]);

  const refreshList = useCallback(async () => {
    try { setSummaries(await listTracks()); } catch { /* ok on first run */ }
  }, []);

  useEffect(() => { refreshList(); }, [refreshList]);

  const onFile = useCallback(async (file: File | undefined) => {
    if (!file) return;
    setLoading(true); setError(null);
    try {
      const track = await importTrack(file);
      setActive(track);
      if (map) {
        addTrackLayer(map, track);
        fitTrackOnMap(map, track);
      }
      await refreshList();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to import track');
    } finally {
      setLoading(false);
    }
  }, [map, refreshList]);

  const onSelectSaved = useCallback(async (id: string) => {
    setLoading(true); setError(null);
    try {
      const track = await loadTrack(id);
      if (!track) throw new Error('Track not found');
      setActive(track);
      if (map) {
        addTrackLayer(map, track);
        fitTrackOnMap(map, track);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load track');
    } finally {
      setLoading(false);
    }
  }, [map]);

  const onDelete = useCallback(async (id: string) => {
    await deleteTrack(id);
    if (active?.id === id) {
      if (map) removeTrackLayer(map);
      setActive(null);
    }
    await refreshList();
  }, [active, map, refreshList]);

  const onClear = useCallback(() => {
    if (map) removeTrackLayer(map);
    setActive(null);
    setScrub(0);
  }, [map]);

  const onScrub = useCallback((value: number) => {
    setScrub(value);
    if (map && active) setOwnshipAtFraction(map, active, value);
    if (active && threeD.ready) threeD.setAircraftAtFraction(active.id, value);
  }, [map, active, threeD]);

  const onDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    void onFile(file);
  }, [onFile]);

  const scrubPoint = active?.points[
    Math.min(active.points.length - 1, Math.floor(scrub * active.points.length))
  ];

  return (
    <div>
      {/* Upload zone */}
      <div
        className="panel-section"
        onDragOver={(e) => { e.preventDefault(); }}
        onDrop={onDrop}
        style={{ border: '1px dashed var(--border, #334155)', borderRadius: 6, padding: 16, textAlign: 'center' }}
      >
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>
          Drop a <b>GPX</b>, <b>KML</b>, <b>CSV/TSV</b>, or <b>GeoJSON</b> flight track here
        </div>
        <button className="btn btn-primary" disabled={loading} onClick={() => fileInputRef.current?.click()}>
          {loading ? 'Importing\u2026' : '\ud83d\udcc2 Choose file'}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".gpx,.kml,.csv,.tsv,.geojson,.json,application/gpx+xml,application/vnd.google-earth.kml+xml,application/geo+json,text/csv"
          style={{ display: 'none' }}
          onChange={(e) => onFile(e.target.files?.[0] ?? undefined)}
        />
        {error && (
          <div style={{ marginTop: 8, fontSize: 11, color: 'var(--danger, #EF4444)' }}>{error}</div>
        )}
      </div>

      {/* Active track details */}
      {active && (
        <div className="panel-section">
          <div className="panel-section-title">
            {active.name}
            <span style={{ color: 'var(--text-muted)', fontWeight: 400, marginLeft: 4 }}>
              ({active.format.toUpperCase()} \u00b7 {active.points.length.toLocaleString()} pts)
            </span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontFamily: 'var(--font-mono, monospace)', fontSize: 12 }}>
            <div><span style={{ color: 'var(--text-muted)' }}>Distance</span><br />{fmtDistance(active.stats.distanceNm)}</div>
            <div><span style={{ color: 'var(--text-muted)' }}>Duration</span><br />{fmtDuration(active.stats.durationMs)}</div>
            <div><span style={{ color: 'var(--text-muted)' }}>Max alt</span><br />{fmtAlt(active.stats.maxAltFt)}</div>
            <div><span style={{ color: 'var(--text-muted)' }}>Max GS</span><br />{fmtSpeed(active.stats.maxGsKts)}</div>
            <div><span style={{ color: 'var(--text-muted)' }}>Climb</span><br />{fmtAlt(active.stats.climbFt)}</div>
            <div><span style={{ color: 'var(--text-muted)' }}>Descent</span><br />{fmtAlt(active.stats.descentFt)}</div>
          </div>

          {/* Scrubber */}
          <div style={{ marginTop: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text-muted)' }}>
              <span>Playback</span>
              <span>
                {scrubPoint?.altitudeFt !== undefined ? fmtAlt(scrubPoint.altitudeFt) : '\u2014'} /
                {' '}
                {scrubPoint?.groundSpeedKts !== undefined ? fmtSpeed(scrubPoint.groundSpeedKts) : '\u2014'}
              </span>
            </div>
            <input
              type="range"
              min={0} max={1000}
              value={Math.round(scrub * 1000)}
              onChange={(e) => onScrub(parseInt(e.target.value, 10) / 1000)}
              style={{ width: '100%' }}
            />
          </div>

          <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
            <button className="btn btn-secondary" onClick={onClear} style={{ flex: 1 }}>Clear from map</button>
          </div>
        </div>
      )}

      {/* Saved tracks list */}
      <div className="panel-section">
        <div className="panel-section-title">
          Saved tracks <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>({summaries.length})</span>
        </div>
        {summaries.length === 0 && (
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>No imported tracks yet.</div>
        )}
        <div style={{ maxHeight: 260, overflowY: 'auto' }}>
          {summaries.map((s) => (
            <div key={s.id} className="list-item" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button
                className="btn btn-ghost"
                style={{ flex: 1, textAlign: 'left', padding: 4 }}
                onClick={() => onSelectSaved(s.id)}
                disabled={loading}
              >
                <div style={{ fontSize: 13 }}>{s.name}</div>
                <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>
                  {s.format.toUpperCase()} \u00b7 {fmtDistance(s.stats.distanceNm)} \u00b7 {new Date(s.importedAt).toLocaleString()}
                </div>
              </button>
              <button className="btn btn-icon" onClick={() => onDelete(s.id)} title="Delete">\ud83d\uddd1\ufe0f</button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
